/**
 * Lead-kind visual identity — single source of truth.
 *
 * Used by Unified Inbox (desktop + mobile), the Lead Detail modal header,
 * and every per-kind LeadsView page header. Changing a colour here
 * propagates across all admin listings — no need to update three files.
 *
 * Design rules:
 * • Each kind has a UNIQUE hue family so admins identify leads at a glance
 *   without reading the text.
 * • DEMO uses a vibrant orange (hot / action-required / parents waiting).
 * • TUTOR uses a calm indigo (professional / recruitment / not time-critical).
 * • INQUIRY uses brand blue (default contact pipeline).
 * • All `text-on-bg` pairs meet WCAG AA contrast (4.5:1 minimum).
 */

export const LEAD_KIND_STYLES = {
    inquiries: {
        label: "Inquiry",
        short: "INQ",
        // Badge worn inside lead cards
        badge: "bg-blue-100 text-blue-800 ring-1 ring-blue-200",
        // Solid dot used in counter strips
        dot: "bg-blue-600",
        // 4px left accent applied to the lead card border
        leftBorder: "border-l-[5px] border-l-blue-500",
        // Soft hover ring for the whole card
        cardHover: "hover:border-blue-300 hover:bg-blue-50/30",
        // Larger accent block (used in detail modal header)
        accentBg: "bg-blue-50",
        accentText: "text-blue-800",
        accentRing: "ring-blue-200",
    },
    registrations: {
        label: "Demo",
        short: "DEMO",
        badge: "bg-orange-100 text-orange-900 ring-1 ring-orange-300",
        dot: "bg-orange-500",
        leftBorder: "border-l-[5px] border-l-orange-500",
        cardHover: "hover:border-orange-300 hover:bg-orange-50/30",
        accentBg: "bg-orange-50",
        accentText: "text-orange-900",
        accentRing: "ring-orange-200",
    },
    "tutor-applications": {
        label: "Tutor",
        short: "TUTOR",
        badge: "bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200",
        dot: "bg-indigo-600",
        leftBorder: "border-l-[5px] border-l-indigo-500",
        cardHover: "hover:border-indigo-300 hover:bg-indigo-50/30",
        accentBg: "bg-indigo-50",
        accentText: "text-indigo-800",
        accentRing: "ring-indigo-200",
    },
};

/** Safe lookup — falls back to inquiries styling when an unknown kind is passed. */
export function styleFor(kind) {
    return LEAD_KIND_STYLES[kind] || LEAD_KIND_STYLES.inquiries;
}
